import React from 'react';
import { useIntl } from 'react-intl';
import images from '../../../assets/images/images';
// import InfoIcon from '../../../assets/images/svg/infoicon';
import { CustomLink, Image } from '../..';
import { ContactPointViewItemType } from '../../../enums';
import { IContactPointViewItem } from '../../../interfaces/contactpoint.interfaces';
import parse from 'react-html-parser';

import styles from './contactPointSearcher.module.scss';

interface IContactPointViewLineProps {
    view: IContactPointViewItem;
    level: number;
    serachInputValue: string;
    onClick: (view: IContactPointViewItem, event: React.SyntheticEvent) => void;
}

export const ContactPointViewLine = ({
    view,
    level,
    serachInputValue,
    onClick,
}: IContactPointViewLineProps): JSX.Element => {
    const { formatMessage } = useIntl();

    const calcSpace = 1.25 * level * 16;
    const enableInfoReport = view?.contactPoint?.reportLink;

    const shouldDisplayDescription = view?.description || view?.contactPoint?.reportLink?.description;

    // A better way of handling focus state would be through component context to avoid playing around
    // with the natural flow of non-react elements. This should be refactored when the tree gets turned into its own component
    const handleViewLineInteraction = (targetView: IContactPointViewItem, event: React.SyntheticEvent): void => {
        const { key, currentTarget } = event as React.KeyboardEvent<HTMLDivElement>;

        const isSelectKey = key === 'Enter';
        const isExpandViewKey = key === 'ArrowRight' || key === 'ArrowLeft';
        const isShiftFocusUpKey = key === 'ArrowUp' && currentTarget.previousElementSibling;
        const isShiftFocusDownKey = key === 'ArrowDown' && currentTarget.nextElementSibling;

        if (isSelectKey) {
            event.preventDefault();
            onClick(targetView, event);
            return;
        }
        if (isShiftFocusDownKey) {
            (currentTarget.nextElementSibling as HTMLElement).focus();
            return;
        }

        if (isShiftFocusUpKey) {
            (currentTarget.previousElementSibling as HTMLElement).focus();
            return;
        }

        if (isExpandViewKey) {
            event.preventDefault();
            onClick(targetView, event);
            return;
        }
    };

    const highLightText = (name: string) => {
        if (level === 0) {
            const regex = new RegExp(serachInputValue, 'gi');
            return name.replace(regex, (match) => `<span class="bold">${match}</span>`);
        }

        return name;
    };

    return !view ? null : (
        <button
            role="treeitem"
            tabIndex={0}
            className={`d-flex flex-row col-lg-12 pl-0 pr-md-4 border-0 ${styles.contactPointViewLine}`}
            onClick={(event): void => onClick(view, event)}
            onKeyDown={(event): void => handleViewLineInteraction(view, event)}
        >
            <div
                className={[styles.toggleExpanderIconWrap, 'd-flex py-2'].join(' ')}
                style={{ marginLeft: `${calcSpace}px`, marginTop: '5px' }}
            >
                {view.type !== ContactPointViewItemType.ContactPoint &&
                    view.type !== ContactPointViewItemType.Static && (
                        <Image
                            src={view.showChildren ? images.expandDarkSmall : images.collapseDarkSmall}
                            className={view.showChildren ? `${styles.arrowIconExpand}` : `${styles.arrowIconCollapse}`}
                            alt="EXPAND"
                        />
                    )}
            </div>

            <div className="d-flex flex-column" style={{ flex: 1 }}>
                <div
                    className={`w-100 align-items-start py-2 px-0 ${
                        view.type === ContactPointViewItemType.Static || !view.active ? styles.static : ''
                    }`}
                    style={{ textAlign: 'justify', fontWeight: 500 }}
                >
                    {serachInputValue === '' ? view.name : parse(highLightText(view.name))}
                    <em>{view.active ? '' : ` (${formatMessage({ id: 'INACTIVE' })})`}</em>
                </div>

                {!shouldDisplayDescription ? null : (
                    <div className={`d-flex flex-shrink-0 ${styles.contactPointDesc}`}>
                        <div className={`${styles.information} text-break`}>
                            <div className={`${styles.textellipsis2}`} title={view.description}>
                                {view.description}
                            </div>

                            {enableInfoReport && (
                                <div>
                                    <div className="my-1">
                                        <div>
                                            <strong>{view.contactPoint.reportLink.label}</strong>
                                        </div>

                                        <div>{view.contactPoint.reportLink.description}</div>
                                    </div>

                                    <div className="mt-3">
                                        <CustomLink
                                            href={view.contactPoint.reportLink.externalLink}
                                            target="_blank"
                                            className="d-inline-block"
                                        >
                                            {view.contactPoint.reportLink.externalLinkText}
                                        </CustomLink>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </button>
    );
};
